import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject } from '@angular/core';
import * as i2 from '@angular/router';
import { NavigationEnd } from '@angular/router';
import { Subscription, Subject, merge } from 'rxjs';
import { filter, map, distinctUntilChanged, startWith, pairwise, first, buffer, mergeAll, skipUntil, withLatestFrom } from 'rxjs/operators';

const GLADY_TRACKING_CONFIGURATION = new InjectionToken('GLADY_TRACKING_CONFIGURATION');

/* eslint-disable @typescript-eslint/naming-convention */
class GladyTrackingSetUserPropertiesEvent {
    constructor(data) {
        this.event = 'setUserProperties';
        Object.assign(this, data);
    }
    assignPageAttributes(attributes) {
        // This is a noop.
    }
}
class GladyTrackingLogBaseEvent {
    constructor(eventType, eventName) {
        this.event = 'logEvent';
        this.page_environment = '';
        this.page_referrer = '';
        this.page_site = '';
        this.page_title = '';
        this.page_url = '';
        this.event_type = eventType;
        this.event_name = eventName;
    }
    assignPageAttributes(attributes) {
        Object.assign(this, attributes);
    }
}
/**
 * Event type for generic events.
 */
class GladyTrackingLogGenericEvent extends GladyTrackingLogBaseEvent {
    constructor(eventName) {
        super('generic', eventName);
    }
}
/**
 * Event type for labelized events.
 */
class GladyTrackingLogLabelizedEvent extends GladyTrackingLogBaseEvent {
    constructor(eventName, label) {
        super('labelized', eventName);
        this.label = label;
    }
}
/**
 * Event type for categorized events.
 */
class GladyTrackingLogCategorizedEvent extends GladyTrackingLogLabelizedEvent {
    constructor(eventName, label, category) {
        super(eventName, label);
        this.category = category;
    }
}
/**
 * Event type for custom events (other event_type that those proposed in the lib, like 'filter', 'menu', ...)
 */
class GladyTrackingLogCustomEvent extends GladyTrackingLogBaseEvent {
    constructor(eventType, eventName, data) {
        super(eventType, eventName);
        Object.assign(this, data);
    }
}

class TrackingTitleService {
    constructor() {
        this.uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    }
    generate(path) {
        if (path === '/') {
            return 'home';
        }
        const pathWithoutQueryParams = path.split('?')[0];
        const segments = pathWithoutQueryParams.split('/');
        const replacedSegments = segments.map((segment) => {
            if (/^\d+$/.test(segment) || this.uuidRegex.test(segment)) {
                return '#';
            }
            return segment;
        });
        return replacedSegments.slice(1).join('_');
    }
}
TrackingTitleService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TrackingTitleService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
TrackingTitleService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TrackingTitleService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TrackingTitleService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

const windowWithDataLayer = window;
/**
 * Service to send events to our tracking systems.
 *
 * Do not forget to inject GLADY_TRACKING_CONFIGURATION with appropriate values (environment and site)
 * at the root module.
 *
 * CAUTION: this service should be used only in standalone frontend.
 * Do not use this service in your microfronts as the shared-info microfront will be
 * managing the tracking with this service (in order to consistently add page information
 * like the url, title and the previous page).
 * In a microfront, use PortalTrackingService from the front-portal-lib instead.
 */
class TrackingService {
    constructor(trackingTitle, router, config) {
        this.trackingTitle = trackingTitle;
        this.router = router;
        this.config = config;
        this.allSubscriptions = new Subscription();
        this.sendLogEventSubject = new Subject();
        this.pageAttributes$ = this.router.events.pipe(filter((event) => event instanceof NavigationEnd), map((event) => `${window.location.origin}${event.url}`), map((urlString) => {
            // Sanitize the URL by removing risky query params, like "code", "token", etc..
            const url = new URL(urlString);
            TrackingService.riskyQueryParams.forEach((riskyQueryParam) => {
                url.searchParams.delete(riskyQueryParam);
            });
            return url.toString();
        }), distinctUntilChanged(), startWith(document.referrer, window.location.href), pairwise(), 
        /* eslint-disable @typescript-eslint/naming-convention */
        map(([oldUrl, newUrl]) => ({
            page_environment: this.config.environment,
            page_title: this.trackingTitle.generate(new URL(newUrl).pathname),
            page_url: newUrl,
            page_referrer: oldUrl,
            page_site: this.config.site,
        }))
        /* eslint-enable @typescript-eslint/naming-convention */
        );
        // Emit the logEvent events with the common page attributes.
        // sendLogEventSubject is the main source observable as the event must
        // be sent when it's triggered (in sendLogEvent).
        // However, in order to send the event, we have to get the current value
        // of the page attributes (this.pageAttributes$).
        //
        // It would be easy to deal with that with a simple withLatestFrom(this.pageAttributes$)
        // but we also want to wait for the first page attributes before sending the
        // logEvent events. It may be a problem if sendLogEvent is called at least once
        // before the Angular router allow us to compute the first page attributes
        //
        // See for reference: https://stackoverflow.com/a/49538619/20214195
        const firstPageAttributes$ = this.pageAttributes$.pipe(first());
        this.allSubscriptions.add(merge(this.sendLogEventSubject.pipe(buffer(firstPageAttributes$), mergeAll()), this.sendLogEventSubject.pipe(skipUntil(firstPageAttributes$)))
            .pipe(withLatestFrom(this.pageAttributes$))
            .subscribe(([event, attributes]) => {
            event.assignPageAttributes(attributes);
            this.sendRaw(event);
        }));
    }
    ngOnDestroy() {
        this.allSubscriptions.unsubscribe();
    }
    /**
     * Init the router tracking with generic event for each page changes.
     */
    initRouterTracking() {
        if (windowWithDataLayer.routerIsTracked) {
            console.warn('[GladyTracking] router is already tracked.\n' +
                'Are you sure you are not using TrackingService (from front-common-lib) in a microfront ' +
                'instead of PortalTrackingService (from front-portal-lib)?');
            return;
        }
        this.allSubscriptions.add(this.pageAttributes$.subscribe((pageAttributes) => {
            const event = new GladyTrackingLogGenericEvent('pageView');
            event.assignPageAttributes(pageAttributes);
            this.sendRaw(event);
        }));
        console.log('[GladyTracking] router is tracked.');
        windowWithDataLayer.routerIsTracked = true;
    }
    /**
     * Send tracking events.
     *
     * @param event
     */
    sendEvent(event) {
        this.sendLogEventSubject.next(event);
    }
    sendRaw(event) {
        windowWithDataLayer.dataLayer = windowWithDataLayer.dataLayer || [];
        // stringify then parse to remove all methods and prototypes from the types.
        windowWithDataLayer.dataLayer.push(JSON.parse(JSON.stringify(event)));
    }
}
TrackingService.riskyQueryParams = [
    'code',
    'token',
    'password',
    'pwd',
];
TrackingService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TrackingService, deps: [{ token: TrackingTitleService }, { token: i2.Router }, { token: GLADY_TRACKING_CONFIGURATION }], target: i0.ɵɵFactoryTarget.Injectable });
TrackingService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TrackingService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TrackingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () {
        return [{ type: TrackingTitleService }, { type: i2.Router }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [GLADY_TRACKING_CONFIGURATION]
                    }] }];
    } });

/**
 * Generated bundle index. Do not edit.
 */

export { GLADY_TRACKING_CONFIGURATION, GladyTrackingLogBaseEvent, GladyTrackingLogCategorizedEvent, GladyTrackingLogCustomEvent, GladyTrackingLogGenericEvent, GladyTrackingLogLabelizedEvent, GladyTrackingSetUserPropertiesEvent, TrackingService, TrackingTitleService };
